import { useEffect, useState, type ChangeEvent } from "react";
import Download from "@mui/icons-material/Download";
import Edit from "@mui/icons-material/Edit";
import MonetizationOn from "@mui/icons-material/MonetizationOn";
import OpenInNew from "@mui/icons-material/OpenInNew";
import PersonAdd from "@mui/icons-material/PersonAdd";
import PersonRemove from "@mui/icons-material/PersonRemove";
import Receipt from "@mui/icons-material/Receipt";
import Visibility from "@mui/icons-material/Visibility";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  Link,
  ListItemIcon,
  Menu,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import axios from "axios";
import { Link as RouterLink } from "react-router-dom";

import { dayjs } from "@thalamos/common";
import type {
  Section12PaymentRequestChange,
  Section12PaymentRequestStatus,
  Section12PaymentRequestWithDetails,
} from "@thalamos/aspire-common";
import {
  ADJUSTABLE_PAYMENT_STATUSES,
  OVERRIDABLE_BLOCK_REASONS,
} from "@thalamos/aspire-common";

import { api, apiHooks } from "../../api.js";
import { routeFns } from "../../routePaths.js";
import {
  buildCsvFileName,
  downloadCsvBlob,
  formatCurrency,
  formatDoctorDisplayName,
  formatPaymentDate,
  STATUS_CHIP_SX,
  STATUS_COLORS,
  STATUS_LABELS_ADMIN,
  UNKNOWN_LOCAL_AUTHORITY_LABEL,
} from "../Section12PaymentRequests/payment-request-helpers.js";
import { PaymentRequestDetailDialog } from "../Section12PaymentRequests/PaymentRequestDetailDialog.js";
import { PdsLinkBadge } from "../Section12PaymentRequests/PdsLinkBadge.js";
import { Section12AccreditationBadge } from "../Section12PaymentRequests/Section12AccreditationBadge.js";
import { AdjustPaymentRequestDialog } from "./AdjustPaymentRequestDialog.js";
import { ConfirmBillOverrideDialog } from "./ConfirmBillOverrideDialog.js";
import { LinkDoctorToXeroDialog } from "./LinkDoctorToXeroDialog.js";
import { UpdatePaymentStatusDialog } from "./UpdatePaymentStatusDialog.js";
import {
  dismissToast,
  LoadingSpinner,
  renderErrorToast,
  renderInfoToast,
  renderSuccessToast,
  renderWarningToast,
} from "~/components/design-system/index.js";
import { assertOkResponse } from "~/helpers/assertOkResponse.js";

/**
 * Keeps a name to a single line so every row stays the same height, with the
 * untruncated value available on hover. The cap must stay on the inner block:
 * a `max-width` on a `td` only limits the column's preferred width, so a
 * nowrap cell still widens the table to fit its text.
 */
function TruncatedCell({ children }: { children: string }) {
  return (
    <TableCell>
      <Tooltip title={children}>
        <Typography variant="body2" noWrap sx={{ maxWidth: 130 }}>
          {children}
        </Typography>
      </Tooltip>
    </TableCell>
  );
}

export function Section12PaymentRequestsManagementPage() {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [statusFilter, setStatusFilter] = useState<
    Section12PaymentRequestStatus | ""
  >("");
  const [fromDate, setFromDate] = useState<string>(
    dayjs().subtract(30, "days").format("YYYY-MM-DD"),
  );
  const [toDate, setToDate] = useState<string>(dayjs().format("YYYY-MM-DD"));
  const [selectedRequest, setSelectedRequest] =
    useState<Section12PaymentRequestWithDetails | null>(null);
  const [updateRequest, setUpdateRequest] =
    useState<Section12PaymentRequestWithDetails | null>(null);
  const [adjustRequest, setAdjustRequest] =
    useState<Section12PaymentRequestWithDetails | null>(null);
  const [changeHistory, setChangeHistory] = useState<
    Section12PaymentRequestChange[]
  >([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isMarkingAsPaid, setIsMarkingAsPaid] = useState(false);
  const [isExportingFinanceCsv, setIsExportingFinanceCsv] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [menuRequest, setMenuRequest] =
    useState<Section12PaymentRequestWithDetails | null>(null);
  const [processingBillRequestIds, setProcessingBillRequestIds] = useState<
    Set<string>
  >(new Set());

  const addProcessingId = (id: string) => {
    setProcessingBillRequestIds((prev) => new Set(prev).add(id));
  };
  const removeProcessingId = (id: string) => {
    setProcessingBillRequestIds((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  };
  const [linkXeroDoctor, setLinkXeroDoctor] = useState<{
    userId: string;
    name: string;
    email: string;
  } | null>(null);
  const [overrideConfirm, setOverrideConfirm] = useState<{
    request: Section12PaymentRequestWithDetails;
    blockReasons: string[];
  } | null>(null);

  const pollForBillCreation = async (
    requestId: string,
  ): Promise<"created" | "timeout"> => {
    const POLL_INTERVAL_MS = 2000;
    const TIMEOUT_MS = 60000;
    const start = Date.now();
    while (Date.now() - start < TIMEOUT_MS) {
      await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
      try {
        const response = await api.section12.getPaymentRequestById(requestId);
        assertOkResponse(response);
        if (response.data.xeroInvoiceId) {
          return "created";
        }
      } catch (error) {
        console.error("Polling for Xero bill creation failed", error);
      }
    }
    return "timeout";
  };

  const handleCreateXeroBill = async (
    request: Section12PaymentRequestWithDetails,
    force = false,
  ) => {
    if (processingBillRequestIds.has(request.id)) return;

    setMenuAnchorEl(null);
    setMenuRequest(null);
    addProcessingId(request.id);

    try {
      const response = await api.section12.createXeroBill(
        request.id,
        force ? { force: true } : undefined,
      );
      assertOkResponse(response);

      if (response.data.queued) {
        const toastId = renderInfoToast({
          message: "Creating Xero bill...",
          autoClose: false,
          showCloseIcon: false,
        });
        const result = await pollForBillCreation(request.id);
        dismissToast(toastId);
        if (result === "created") {
          renderSuccessToast({
            message: "Xero bill created successfully.",
          });
        } else {
          renderWarningToast({
            message:
              "Xero bill is taking longer than expected. Check back shortly.",
          });
        }
      } else {
        const reasons = response.data.blockReasons;
        const allOverridable = reasons.every((r) =>
          (OVERRIDABLE_BLOCK_REASONS as readonly string[]).includes(r),
        );

        if (allOverridable && reasons.length > 0 && !force) {
          removeProcessingId(request.id);
          setOverrideConfirm({ request, blockReasons: reasons });
          return;
        } else {
          renderErrorToast({
            message: `Bill creation blocked: ${reasons.join(", ")}`,
          });
        }
      }
    } catch (error: unknown) {
      console.error("Failed to create Xero bill", error);
      let reason = "Failed to create Xero bill. Please try again.";
      if (axios.isAxiosError(error)) {
        const data = error.response?.data as { reason?: string } | undefined;
        if (typeof data?.reason === "string") {
          reason = data.reason;
        }
      }
      renderErrorToast({ message: reason });
    } finally {
      removeProcessingId(request.id);
      void refetch();
    }
  };

  const [{ data, loading }, refetch] = apiHooks.section12.getAllPaymentRequests(
    {
      status: statusFilter || undefined,
      fromDate: fromDate || undefined,
      toDate: toDate || undefined,
      limit: rowsPerPage,
      offset: page * rowsPerPage,
    },
  );

  // Load change history when a detail dialog is opened
  useEffect(() => {
    if (!selectedRequest) {
      setChangeHistory([]);
      return;
    }

    let cancelled = false;
    api.section12
      .getPaymentRequestChangeHistory(selectedRequest.id)
      .then((response) => {
        assertOkResponse(response);
        if (!cancelled) {
          setChangeHistory(response.data);
        }
      })
      .catch((error: unknown) => {
        console.error("Failed to load change history", error);
        if (!cancelled) {
          setChangeHistory([]);
          renderErrorToast({
            message: "Failed to load payment request change history.",
          });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [selectedRequest]);

  const handleMarkSelectedAsPaid = async () => {
    if (selectedIds.size === 0) return;

    const count = selectedIds.size;
    if (
      !window.confirm(
        `Are you sure you want to mark ${String(count)} payment request${count > 1 ? "s" : ""} as paid?`,
      )
    ) {
      return;
    }

    setIsMarkingAsPaid(true);
    let successCount = 0;
    let failCount = 0;

    for (const paymentRequestId of selectedIds) {
      try {
        await api.section12.markPaymentRequestPaid(paymentRequestId);
        successCount++;
      } catch (error) {
        console.error(
          "Failed to mark payment request as paid:",
          paymentRequestId,
          error,
        );
        failCount++;
      }
    }

    setIsMarkingAsPaid(false);
    setSelectedIds(new Set());

    if (failCount === 0) {
      renderSuccessToast({
        message: `${String(successCount)} payment request${successCount > 1 ? "s" : ""} marked as paid`,
      });
    } else if (successCount === 0) {
      renderErrorToast({
        message: `Failed to mark ${String(failCount)} payment request${failCount > 1 ? "s" : ""} as paid`,
      });
    } else {
      renderSuccessToast({
        message: `${String(successCount)} marked as paid, ${String(failCount)} failed`,
      });
    }

    void refetch();
  };

  const handleSelectAll = (
    checked: boolean,
    selectable: Section12PaymentRequestWithDetails[],
  ) => {
    if (checked) {
      setSelectedIds(new Set(selectable.map((r) => r.id)));
    } else {
      setSelectedIds(new Set());
    }
  };

  const handleSelectOne = (id: string, checked: boolean) => {
    const newSelected = new Set(selectedIds);
    if (checked) {
      newSelected.add(id);
    } else {
      newSelected.delete(id);
    }
    setSelectedIds(newSelected);
  };

  const handleExportCsv = async () => {
    try {
      const response = await api.section12.exportPaymentRequestsCsv({
        status: statusFilter || undefined,
        fromDate: fromDate || undefined,
        toDate: toDate || undefined,
      });
      const blob = new Blob([response.data], { type: "text/csv" });
      downloadCsvBlob(
        blob,
        buildCsvFileName(
          "s12-payment-requests",
          fromDate || undefined,
          toDate || undefined,
        ),
      );
      renderSuccessToast({ message: "CSV downloaded successfully" });
    } catch (error) {
      console.error("Failed to export CSV:", error);
      renderErrorToast({ message: "Failed to export CSV" });
    }
  };

  const handleExportFinanceCsv = async () => {
    setIsExportingFinanceCsv(true);
    try {
      const response = await api.section12.exportFinancePaymentRequestsCsv({
        status: statusFilter || undefined,
        fromDate: fromDate || undefined,
        toDate: toDate || undefined,
      });
      const blob = new Blob([response.data], { type: "text/csv" });
      downloadCsvBlob(
        blob,
        buildCsvFileName(
          "finance-payment-requests",
          fromDate || undefined,
          toDate || undefined,
        ),
      );
      renderSuccessToast({ message: "CSV downloaded successfully" });
    } catch (error) {
      console.error("Failed to export finance CSV:", error);
      renderErrorToast({ message: "Failed to export finance CSV" });
    } finally {
      setIsExportingFinanceCsv(false);
    }
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  if (loading && !data) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "200px",
        }}
      >
        <LoadingSpinner label="Loading payment requests..." />
      </Box>
    );
  }

  const paymentRequests = data?.requests ?? [];
  const totalCount = data?.count ?? 0;

  // Get only accepted requests that can be marked as paid
  const selectableRequests = paymentRequests.filter(
    (r: Section12PaymentRequestWithDetails) => r.status === "accepted",
  );

  const allSelectableChecked =
    selectableRequests.length > 0 &&
    selectableRequests.every((r: Section12PaymentRequestWithDetails) =>
      selectedIds.has(r.id),
    );
  const someSelectableChecked = selectableRequests.some(
    (r: Section12PaymentRequestWithDetails) => selectedIds.has(r.id),
  );

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>
        Section 12 Payment Requests
      </Typography>
      <Typography
        variant="body2"
        sx={{
          color: "text.secondary",
          mb: 2,
        }}
      >
        Manage payment requests submitted by Section 12 doctors for their
        assessments.
      </Typography>
      {/* Filters */}
      <Box
        sx={{
          display: "flex",
          gap: 2,
          mb: 2,
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel id="status-filter-label">Status</InputLabel>
          <Select
            labelId="status-filter-label"
            value={statusFilter}
            label="Status"
            onChange={(e) => {
              setStatusFilter(
                e.target.value as Section12PaymentRequestStatus | "",
              );
              setPage(0);
            }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="pending">Pending AMHP approval</MenuItem>
            <MenuItem value="accepted">Approved by AMHP</MenuItem>
            <MenuItem value="rejected">Rejected by AMHP</MenuItem>
            <MenuItem value="paid">Paid</MenuItem>
            <MenuItem value="payment-processing">Payment Processing</MenuItem>
            <MenuItem value="payment-on-hold">Payment on hold</MenuItem>
            <MenuItem value="receipt-missing">Receipt Missing</MenuItem>
            <MenuItem value="bank-information-missing">
              Bank Information Missing
            </MenuItem>
            <MenuItem value="value-incorrect">Value Incorrect</MenuItem>
            <MenuItem value="registration-with-icb-needed">
              Registration with ICB needed
            </MenuItem>
          </Select>
        </FormControl>

        <TextField
          label="From Date"
          type="date"
          size="small"
          value={fromDate}
          onChange={(e) => {
            setFromDate(e.target.value);
            setPage(0);
          }}
          slotProps={{
            inputLabel: { shrink: true },
          }}
        />

        <TextField
          label="To Date"
          type="date"
          size="small"
          value={toDate}
          onChange={(e) => {
            setToDate(e.target.value);
            setPage(0);
          }}
          slotProps={{
            inputLabel: { shrink: true },
          }}
        />

        <Button
          variant="outlined"
          startIcon={<Download />}
          onClick={() => {
            void handleExportCsv();
          }}
          size="small"
        >
          Export CSV
        </Button>

        <Button
          variant="outlined"
          startIcon={<Download />}
          onClick={() => {
            void handleExportFinanceCsv();
          }}
          size="small"
          // The export walks every matching payment request, so a second click
          // starts a second full scan rather than doing nothing.
          disabled={isExportingFinanceCsv}
        >
          {isExportingFinanceCsv ? "Exporting…" : "Finance Team CSV"}
        </Button>

        {selectedIds.size > 0 && (
          <Button
            variant="contained"
            color="success"
            onClick={() => {
              void handleMarkSelectedAsPaid();
            }}
            disabled={isMarkingAsPaid}
            size="small"
          >
            {isMarkingAsPaid
              ? "Processing..."
              : `Mark ${String(selectedIds.size)} as Paid`}
          </Button>
        )}
      </Box>
      {/* Table */}
      <TableContainer component={Paper}>
        <Table
          size="small"
          // Tighter than the MUI default so the full column set clears a laptop
          // viewport, buying the width from the gutters rather than from the
          // cell contents.
          sx={{ "& .MuiTableCell-root": { px: 1 } }}
        >
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={someSelectableChecked && !allSelectableChecked}
                  checked={allSelectableChecked}
                  onChange={(e) => {
                    handleSelectAll(e.target.checked, selectableRequests);
                  }}
                  disabled={selectableRequests.length === 0}
                  inputProps={{ "aria-label": "select all accepted requests" }}
                />
              </TableCell>
              <TableCell>Reference</TableCell>
              <TableCell>Doctor</TableCell>
              <TableCell>S12 Accreditation</TableCell>
              <TableCell>AMHP Team</TableCell>
              <TableCell>Local Authority</TableCell>
              <TableCell>PDS Link</TableCell>
              <TableCell align="right">Total</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Created</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paymentRequests.length === 0 ? (
              <TableRow>
                <TableCell colSpan={11} align="center">
                  No payment requests found
                </TableCell>
              </TableRow>
            ) : (
              paymentRequests.map(
                (request: Section12PaymentRequestWithDetails) => (
                  <TableRow
                    key={request.id}
                    selected={selectedIds.has(request.id)}
                    sx={
                      processingBillRequestIds.has(request.id)
                        ? { opacity: 0.5, pointerEvents: "none" }
                        : undefined
                    }
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedIds.has(request.id)}
                        onChange={(e) => {
                          handleSelectOne(request.id, e.target.checked);
                        }}
                        disabled={
                          request.status !== "accepted" ||
                          processingBillRequestIds.has(request.id)
                        }
                        inputProps={{
                          "aria-label": `select ${request.doctor ? formatDoctorDisplayName(request.practitionerSnapshot?.fullName ?? request.doctor.name, request.doctor.teamName, request.doctor.teamType) : "Unknown Doctor"}`,
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      <Typography
                        component="span"
                        variant="caption"
                        sx={{ fontFamily: "monospace" }}
                      >
                        {request.paymentReference}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {request.doctor ? (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            maxWidth: 150,
                          }}
                        >
                          <Link
                            component={RouterLink}
                            to={routeFns.userProfilePage(request.doctor.id)}
                            underline="hover"
                            variant="body2"
                            noWrap
                            // Flex items refuse to shrink below their content
                            // width unless min-width is cleared, which would
                            // defeat the cap on the surrounding box.
                            sx={{ minWidth: 0 }}
                          >
                            {formatDoctorDisplayName(
                              request.practitionerSnapshot?.fullName ??
                                request.doctor.name,
                              request.doctor.teamName,
                              request.doctor.teamType,
                            )}
                          </Link>
                          {request.doctor.xeroContactId && (
                            <Link
                              href={`https://go.xero.com/app/!fj07t/contacts/contact/${request.doctor.xeroContactId}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              title="View in Xero"
                              sx={{
                                display: "inline-flex",
                                lineHeight: 1,
                                flexShrink: 0,
                              }}
                            >
                              <OpenInNew
                                sx={{ fontSize: 14, color: "text.secondary" }}
                              />
                            </Link>
                          )}
                        </Box>
                      ) : (
                        <Typography variant="body2" noWrap>
                          Unknown Doctor
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      <Section12AccreditationBadge request={request} />
                    </TableCell>
                    <TruncatedCell>
                      {request.amhpTeam?.name ?? "Unknown Team"}
                    </TruncatedCell>
                    <TruncatedCell>
                      {request.localAuthority?.name ??
                        UNKNOWN_LOCAL_AUTHORITY_LABEL}
                    </TruncatedCell>
                    <TableCell>
                      <PdsLinkBadge
                        hasUsablePdsLink={request.hasUsablePdsLink}
                      />
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                      <Typography variant="body2">
                        {formatCurrency(request.totalAmountPence)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={STATUS_LABELS_ADMIN[request.status]}
                        color={STATUS_COLORS[request.status]}
                        sx={STATUS_CHIP_SX[request.status]}
                        size="small"
                      />
                    </TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      <Typography variant="body2">
                        {formatPaymentDate(request.created)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>
                      {processingBillRequestIds.has(request.id) ? (
                        <CircularProgress size={24} />
                      ) : (
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={(e) => {
                            setMenuAnchorEl(e.currentTarget);
                            setMenuRequest(request);
                          }}
                        >
                          Action
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ),
              )
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 25, 50]}
        component="div"
        count={totalCount}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={() => {
          setMenuAnchorEl(null);
          setMenuRequest(null);
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        <MenuItem
          onClick={() => {
            setSelectedRequest(menuRequest);
            setMenuAnchorEl(null);
            setMenuRequest(null);
          }}
        >
          <ListItemIcon>
            <Visibility fontSize="small" color="primary" />
          </ListItemIcon>
          View Details
        </MenuItem>
        {menuRequest &&
          menuRequest.status !== "paid" &&
          menuRequest.status !== "pending" &&
          menuRequest.status !== "rejected" && (
            <MenuItem
              onClick={() => {
                setUpdateRequest(menuRequest);
                setMenuAnchorEl(null);
                setMenuRequest(null);
              }}
            >
              <ListItemIcon>
                <Edit fontSize="small" color="primary" />
              </ListItemIcon>
              Update Status
            </MenuItem>
          )}
        {menuRequest &&
          (ADJUSTABLE_PAYMENT_STATUSES as readonly string[]).includes(
            menuRequest.status,
          ) && (
            <MenuItem
              onClick={() => {
                setAdjustRequest(menuRequest);
                setMenuAnchorEl(null);
                setMenuRequest(null);
              }}
            >
              <ListItemIcon>
                <MonetizationOn fontSize="small" color="primary" />
              </ListItemIcon>
              Adjust Payment Request
            </MenuItem>
          )}
        {menuRequest &&
          menuRequest.status === "accepted" &&
          !menuRequest.xeroInvoiceId && (
            <MenuItem
              disabled={
                processingBillRequestIds.has(menuRequest.id) ||
                !menuRequest.doctor?.xeroContactId
              }
              onClick={() => void handleCreateXeroBill(menuRequest)}
            >
              <ListItemIcon>
                {processingBillRequestIds.has(menuRequest.id) ? (
                  <CircularProgress size={20} />
                ) : (
                  <Receipt fontSize="small" color="primary" />
                )}
              </ListItemIcon>
              Create Xero Bill
            </MenuItem>
          )}
        {menuRequest?.doctor && !menuRequest.doctor.xeroContactId && (
          <MenuItem
            onClick={() => {
              if (menuRequest.doctor) {
                setLinkXeroDoctor({
                  userId: menuRequest.doctor.id,
                  name: menuRequest.doctor.name,
                  email: menuRequest.doctor.email,
                });
              }
              setMenuAnchorEl(null);
              setMenuRequest(null);
            }}
          >
            <ListItemIcon>
              <PersonAdd fontSize="small" color="primary" />
            </ListItemIcon>
            Link to Xero
          </MenuItem>
        )}
        {menuRequest?.doctor?.xeroContactId && (
          <MenuItem
            onClick={() => {
              if (
                menuRequest.doctor?.xeroContactId &&
                window.confirm(
                  `Remove the Xero link for ${menuRequest.doctor.name}? This will not delete the contact in Xero.`,
                )
              ) {
                void api.configuration
                  .setUserXeroContactId(menuRequest.doctor.id, null)
                  .then(() => {
                    renderSuccessToast({
                      message: "Xero link removed successfully",
                    });
                    void refetch();
                  })
                  .catch((error: unknown) => {
                    console.error("Failed to remove Xero link", error);
                    renderErrorToast({
                      message: "Failed to remove Xero link",
                    });
                  });
              }
              setMenuAnchorEl(null);
              setMenuRequest(null);
            }}
          >
            <ListItemIcon>
              <PersonRemove fontSize="small" color="error" />
            </ListItemIcon>
            Remove Xero Link
          </MenuItem>
        )}
        {menuRequest?.xeroInvoiceId && (
          <MenuItem
            component="a"
            href={`https://go.xero.com/AccountsPayable/View.aspx?InvoiceID=${menuRequest.xeroInvoiceId}`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => {
              setMenuAnchorEl(null);
              setMenuRequest(null);
            }}
          >
            <ListItemIcon>
              <OpenInNew fontSize="small" color="primary" />
            </ListItemIcon>
            Open in Xero
          </MenuItem>
        )}
      </Menu>
      <PaymentRequestDetailDialog
        request={selectedRequest}
        onClose={() => {
          setSelectedRequest(null);
        }}
        onUpdateStatus={(req) => {
          setSelectedRequest(null);
          setUpdateRequest(req);
        }}
        onAdjustPaymentRequest={(req) => {
          setSelectedRequest(null);
          setAdjustRequest(req);
        }}
        changeHistory={changeHistory}
      />
      <UpdatePaymentStatusDialog
        request={updateRequest}
        onClose={() => {
          setUpdateRequest(null);
        }}
        onSuccess={() => {
          setUpdateRequest(null);
          void refetch();
        }}
      />
      <AdjustPaymentRequestDialog
        request={adjustRequest}
        adjustPaymentRequest={api.section12.adjustPaymentRequest}
        onClose={() => {
          setAdjustRequest(null);
        }}
        onSuccess={() => {
          setAdjustRequest(null);
          void refetch();
        }}
      />
      <LinkDoctorToXeroDialog
        open={linkXeroDoctor !== null}
        doctorUserId={linkXeroDoctor?.userId ?? ""}
        doctorName={linkXeroDoctor?.name ?? ""}
        doctorEmail={linkXeroDoctor?.email ?? ""}
        onClose={() => {
          setLinkXeroDoctor(null);
        }}
        onSuccess={() => {
          setLinkXeroDoctor(null);
          void refetch();
        }}
      />
      <ConfirmBillOverrideDialog
        open={overrideConfirm !== null}
        blockReasons={overrideConfirm?.blockReasons ?? []}
        onClose={() => {
          setOverrideConfirm(null);
        }}
        onConfirm={() => {
          if (overrideConfirm) {
            void handleCreateXeroBill(overrideConfirm.request, true);
          }
          setOverrideConfirm(null);
        }}
      />
    </Box>
  );
}
